import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useCancelLesson, useMyReservations, reservationError } from "@/api/queries";
import { Button, Card } from "@/design-system";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/auth";
import {
  formatDay,
  formatDuration,
  formatTime,
  formatWeekday,
} from "@/lib/format";
import { isBookingsClosed } from "@/lib/booking";
import { instructorLabel } from "@/lib/instructor";
import { useOnline } from "@/lib/useOnline";
import { OfflineErrorState } from "@/components/OfflineErrorState";
import screen from "@/layout/screen.module.css";

/**
 * /my-reservations — the student's upcoming bookings.
 *
 * Reads from `useMyReservations` (react-query against
 * `GET /api/me/reservations`) and exposes a Cancel action on every
 * row. The mutation invalidates both the lessons list and the
 * reservations list, so the cache stays consistent on success.
 */
export function MyReservationsScreen() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const online = useOnline();

  const reservationsQuery = useMyReservations(token);
  const cancelMutation = useCancelLesson(token);

  const reservations = reservationsQuery.data ?? [];

  function cancel(lessonId: number) {
    cancelMutation.mutate(lessonId);
  }

  return (
    <div className={screen.capped} style={{ paddingBottom: 96 }} data-testid="my-reservations-screen">
      {/* header */}
      <div
        style={{
          padding: "20px 20px 8px",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label={t("reservations.ariaBack")}
          style={{
            width: 40,
            height: 40,
            borderRadius: 999,
            border: "none",
            background: "var(--surface-card)",
            boxShadow: "var(--shadow-xs)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "var(--text-strong)",
          }}
        >
          <Icon name="arrow-left" size={20} />
        </button>
        <div style={{ flex: 1 }}>
          <div
            style={{
              font: "var(--fw-regular) var(--fs-xs)/1 var(--font-body)",
              color: "var(--text-faint)",
            }}
          >
            {t("reservations.eyebrow")}
          </div>
          <div
            style={{
              font: "var(--fw-extra) var(--fs-2xl)/1.1 var(--font-display)",
              color: "var(--text-strong)",
              letterSpacing: "var(--ls-tight)",
              marginTop: 4,
            }}
          >
            {t("reservations.title")}
          </div>
        </div>
      </div>

      <div
        style={{
          padding: "12px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {reservationsQuery.isLoading && (
          <div
            data-testid="reservations-loading"
            style={{
              padding: "var(--space-6)",
              textAlign: "center",
              color: "var(--text-faint)",
            }}
          >
            {t("reservations.loading")}
          </div>
        )}

        {!reservationsQuery.isLoading && reservationsQuery.isError && (
          online ? (
            <div
              role="alert"
              data-testid="reservations-error"
              style={{
                background: "var(--status-danger-bg)",
                color: "var(--status-danger-fg)",
                padding: "var(--space-4)",
                borderRadius: "var(--radius-md)",
                font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              }}
            >
              {reservationsQuery.error?.message ?? t("reservations.error")}
            </div>
          ) : (
            <OfflineErrorState />
          )
        )}

        {!reservationsQuery.isLoading &&
          !reservationsQuery.isError &&
          reservations.length === 0 && (
            <Card
              padding="var(--space-6)"
              data-testid="reservations-empty"
              style={{ textAlign: "center" }}
            >
              <div
                style={{
                  font: "var(--fw-bold) var(--fs-lg)/1.2 var(--font-display)",
                  color: "var(--text-strong)",
                  marginBottom: 6,
                }}
              >
                {t("reservations.emptyTitle")}
              </div>
              <div
                style={{
                  font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
                  color: "var(--text-muted)",
                  marginBottom: "var(--space-4)",
                }}
              >
                {t("reservations.emptyBody")}
              </div>
              <Button
                variant="primary"
                size="md"
                onClick={() => navigate("/calendar")}
                data-testid="reservations-empty-go-to-schedule"
              >
                {t("reservations.browseSchedule")}
              </Button>
            </Card>
          )}

        {!reservationsQuery.isLoading &&
          !reservationsQuery.isError &&
          reservations.map((c) => {
            const isPending = cancelMutation.isPending && cancelMutation.variables === c.id;
            const startDate = new Date(c.starts_at);
            const closed = isBookingsClosed(c, new Date());
            return (
              <Card
                key={c.id}
                data-testid={`reservation-${c.id}`}
                padding="var(--space-4)"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    // If the three parts cannot fit on one line (very
                    // narrow viewports), the action column wraps onto
                    // its own line instead of overflowing the card.
                    flexWrap: "wrap",
                  }}
                >
                  {/* date block */}
                  <div
                    style={{
                      width: 56,
                      flex: "none",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "10px 6px",
                      borderRadius: "var(--radius-md)",
                      background: "var(--brand-soft)",
                      color: "var(--brand-press)",
                    }}
                  >
                    <span
                      style={{
                        font: "var(--fw-semibold) var(--fs-3xs)/1 var(--font-body)",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {formatWeekday(startDate)}
                    </span>
                    <span
                      style={{
                        font: "var(--fw-bold) var(--fs-xl)/1 var(--font-mono)",
                        marginTop: 2,
                      }}
                    >
                      {formatDay(startDate)}
                    </span>
                  </div>

                  {/* details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        font: "var(--fw-bold) var(--fs-md)/1.2 var(--font-display)",
                        color: "var(--text-strong)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.title}
                    </div>
                    <div
                      style={{
                        font: "var(--fw-regular) var(--fs-xs)/1.3 var(--font-body)",
                        color: "var(--text-muted)",
                        marginTop: 4,
                      }}
                    >
                      {formatTime(c.starts_at)} · {formatDuration(c.duration_minutes)} ·{" "}
                      {instructorLabel(c)}
                    </div>
                    <div
                      style={{
                        font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
                        color: "var(--text-faint)",
                        marginTop: 4,
                      }}
                    >
                      {t("reservations.spotsOfCapacity", {
                        spotsLeft: c.spots_left,
                        capacity: c.capacity,
                      })}
                    </div>
                  </div>

                  {/* cancel — disabled past the booking cutoff (issue #36) */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 4,
                      // `minWidth: 0` lets the column shrink below the
                      // min-content of the long "cancellations close"
                      // note (which then wraps), so the row never
                      // overflows the card on narrow screens; the
                      // button itself stays intact inside the column.
                      minWidth: 0,
                      flexShrink: 1,
                    }}
                  >
                    <Button
                      variant="soft"
                      size="sm"
                      onClick={() => cancel(c.id)}
                      disabled={isPending || closed || !online}
                      title={!online ? t("offline.bookingDisabled") : undefined}
                      data-testid={`reservation-${c.id}-cancel`}
                    >
                      {closed
                        ? t("reservations.cancelClosed")
                        : isPending
                          ? t("common.cancelling")
                          : t("common.cancel")}
                    </Button>
                    {closed && (
                      <span
                        data-testid={`reservation-${c.id}-cancel-closed`}
                        style={{
                          font: "var(--fw-regular) var(--fs-2xs)/1.3 var(--font-body)",
                          color: "var(--text-faint)",
                          textAlign: "right",
                        }}
                      >
                        {t("reservations.cancelClosedNote")}
                      </span>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}

        {cancelMutation.isError && (
          <div
            role="alert"
            data-testid="reservations-cancel-error"
            style={{
              background: "var(--status-danger-bg)",
              color: "var(--status-danger-fg)",
              padding: "var(--space-4)",
              borderRadius: "var(--radius-md)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
            }}
          >
            {reservationError(cancelMutation.error) === "too_late"
              ? t("reservations.cancelTooLate")
              : cancelMutation.error?.message ?? t("reservations.cancelError")}
          </div>
        )}
      </div>
    </div>
  );
}
